import { Country, Store } from '../types';
import { iDeliveryLocation } from '../types';
import LocalStorage from '../helper/storage';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

type Location = {
    latitude: number;
    longitude: number;
}

interface GeolocationContextProps {
    userAddress: string;
    location: Location;
    deliveryLocations: iDeliveryLocation[];
    findClosestStoreToCustomer: (stores: Store[], userLocation: iDeliveryLocation) => Store[];
    handleFetchLocationFromAddress: (userLocation: iDeliveryLocation) => Promise<iDeliveryLocation | undefined>;
    saveUserLocationAddress: (userLocation: iDeliveryLocation) => void;
}

interface GeolocationStorage {
    userAddress: string;
    location: Location;
}

const GeolocationContext = createContext<GeolocationContextProps|undefined>(undefined);

export const GeolocationProvider = ({ children }: {children: React.ReactNode}) => {

    const [userAddress, setUserAddress] = useState<string>('');
    const initialLoad = useRef(false);

    const [location, setLocation] = useState<Location>({ latitude: 0, longitude: 0 });
    const [deliveryLocations, setDeliveryLocations] = useState<iDeliveryLocation[]>([]);


    // useEffect(() => {
    //     const savedLocation = LocalStorage.getItem<GeolocationStorage>('location');
    
    //     if (savedLocation?.location && savedLocation?.userAddress) {
    //       setLocation(savedLocation.location);
    //       setUserAddress(savedLocation.userAddress);
    //     } else {
    //       void handleFetchGeolocation();
    //     }
    // }, []);

    const getDeliveryLocations = useCallback(async () => {
        try {
            const savedLocations = await LocalStorage.getItem<iDeliveryLocation[]>('deli-locations');
            console.log("savedLocations", savedLocations);
    
            if (savedLocations && savedLocations.length > 0) {
                setDeliveryLocations(savedLocations);
            }
            else {
                console.log("No delivery locations found in LocalStorage.");
                void handleFetchGeolocation();
            }
        } catch (error) {
            console.log("No delivery locations found in LocalStorage.");
            void handleFetchGeolocation();
        }
    }, []);

    useEffect(() => {
        void getDeliveryLocations();
    }, []);



    useEffect(() => {
        if (location.latitude && location.longitude && userAddress) {
            const newLocation: GeolocationStorage = { userAddress, location };
            LocalStorage.saveItem('location', newLocation);

            const updatedLocation: iDeliveryLocation = {
                id: new Date().getTime(),
                address: userAddress,
                country: Country.nigeria,
                latitude: location.latitude,
                longitude: location.longitude,
                defaults: true,
            };
            saveUserLocationAddress(updatedLocation);
        }
    }, [location, userAddress]);

    useEffect(() => {
        if (!userAddress && location.latitude && location.longitude) {
          void handleFetchAddress();
        }
    }, [location.latitude, location.longitude]);


    const handleGeolocationFallback = useCallback(async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_IP_ADDRESS_API}`);
            const data = await res.json();
            const { latitude, longitude } = data;

            setLocation({latitude,longitude});
          } catch (error) {
            throw new Error("IP geolocation failed:" + error);
          }
    }, [])

    const handleFetchGeolocation = useCallback(() => {
        try {
            
            navigator.geolocation.getCurrentPosition((position) => 
                {
                    setLocation({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude
                    });

                }, (error) => {
                    console.error('Geolocation error:' + error + '. Fallback to IP geolocation.');
                    // If geolocation fails, fallback to IP geolocation
                    void handleGeolocationFallback();
                },
                {
                    enableHighAccuracy: true,
                    maximumAge: 0,
                }
            )

        } catch (error) {
            throw new Error("Permission rejected.");
        }

    }, []);


    const handleFetchAddress = useCallback(async () => {

        if (!location.latitude || !location.longitude) {
            return ;
        }

        const { latitude, longitude } = location;

        const abortcontroller = new AbortController();

        const fullGeocodeUrl = `${process.env.REACT_APP_GEOCODE_API}/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GEOCODE_API_KEY}`;

        const response = await fetch(fullGeocodeUrl, {
            signal: abortcontroller.signal
        });

        const jsonResponse = await response.json();

        console.log("handleFetchAddress :: jsonResponse", jsonResponse);


        if (jsonResponse.status !== 'OK' || !jsonResponse.results || jsonResponse.results.length === 0) {
            throw new Error("Error fetching address:", jsonResponse.status);
        }

        // Assuming the first result is the most relevant
        if (!jsonResponse.results[0].formatted_address) {
            throw new Error("No formatted address found in response");
        }

        // Extract the address from the response
        const address = jsonResponse.results[0].formatted_address;

        const updatedLocation: iDeliveryLocation = {
            id: new Date().getTime(),
            address,
            country: Country.nigeria,
            latitude,
            longitude,
            defaults: true,
        };

        saveUserLocationAddress(updatedLocation);
        // setUserAddress(address);

    }, [location.latitude, location.longitude]);


    const handleFetchLocationFromAddress = useCallback(async (userLocation: iDeliveryLocation): Promise<iDeliveryLocation | undefined> => {

        if (!userLocation.address) {
            return ;
        }

        const abortcontroller = new AbortController();

        let locations = null;
        
        try {

            const fullGeocodeUrl = `${process.env.REACT_APP_GEOCODE_API}/geocode/json?address=${encodeURIComponent(userLocation.address)}&key=${process.env.REACT_APP_GEOCODE_API_KEY}`;
    
            const response = await fetch(fullGeocodeUrl, {
                signal: abortcontroller.signal
            });
    
            const jsonResponses = await response.json();

            if (jsonResponses.status !== 'OK' || !jsonResponses.results || jsonResponses.results.length === 0) {
                throw new Error("Error fetching address:", jsonResponses.status);
            }

            // Assuming the first result is the most relevant
            if (!jsonResponses.results[0].geometry) {
                throw new Error("No formatted address found in response");
            }

            const { location } = jsonResponses.results[0].geometry;

            locations = {...location};

        } catch (error) {
            throw new Error("Error fetching location from address:" + error);
        }

        if (!locations) {
            throw new Error("Error fetching location from address");
        }

        const newUserLocationWithIP: iDeliveryLocation = {...userLocation, latitude: Number(locations.lat), longitude: Number(locations.lng) };

        return newUserLocationWithIP;

    }, []);


    const haversineDistance = (userLat: number, userLon: number, otherLat: number, otherLon: number) => {
        const toRad = (x: number) => x * Math.PI / 180;
      
        const R = 6371; // Radius of the Earth in km
        const dLat = toRad(otherLat - userLat);
        const dLon = toRad(otherLon - userLon);
        const a =
          Math.sin(dLat / 2) ** 2 +
          Math.cos(toRad(userLat)) * Math.cos(toRad(otherLat)) * Math.sin(dLon / 2) ** 2;
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c; // Distance in km
    };


    const findClosestStoreToCustomer = useCallback((
    stores: Store[],
    userLocation: iDeliveryLocation
    ): Store[] => {
        
    if (!userLocation.latitude || !userLocation.longitude) {
        return stores;
    }

    const { latitude, longitude } = userLocation;

    const sorted = [...stores].sort((a, b) => {
        const distA = haversineDistance(
        latitude,
        longitude,
        a.latitude,
        a.longitude
        );
        const distB = haversineDistance(
        latitude,
        longitude,
        b.latitude,
        b.longitude
        );
        return distA - distB;
    });

    return sorted;
    }, []);



    const saveUserLocationAddress = useCallback(async (updatedLocation: iDeliveryLocation) => {

        try {
            
            // Retrieve existing locations from LocalStorage
            const locations = await LocalStorage.getItem<iDeliveryLocation[]>('deli-locations') || [];
    
            // Set all existing locations to non-default
            const modifiedLocations = locations.map((location) => ({
                ...location,
                defaults: false,
            }));
    
    
            // Find if the location already exists in the array
            const existingLocationIndex = locations.findIndex((location) => location.address === updatedLocation.address);
    
            let newLocations: iDeliveryLocation[];
    
            if (existingLocationIndex !== -1) {
                // Update the existing location if found
                modifiedLocations[existingLocationIndex] = { 
                    ...modifiedLocations[existingLocationIndex], 
                    ...updatedLocation 
                };
    
                newLocations = [...modifiedLocations]; // No need to add it again
    
            } else {

                // Add the updated location to the array
                // Check if the updated location has valid coordinates
                // If not, fetch the coordinates from the address
                // and create a new object with the updated location
                // Otherwise, just add the updated location
                // to the array
                // Create a new object with the updated location
                // and the default value set to true
                let newUpdatedLocation: iDeliveryLocation = { ...updatedLocation };

                if (newUpdatedLocation.longitude === 0 && newUpdatedLocation.latitude === 0) {
                    
                    const locationUpdate = await handleFetchLocationFromAddress(updatedLocation);

                    if (locationUpdate && locationUpdate?.longitude && locationUpdate?.latitude) {
                        newUpdatedLocation = { ...updatedLocation, ...locationUpdate };
                    }
                }

                // If the location doesn't exist, add the updated location to the array
                newLocations = [...modifiedLocations, newUpdatedLocation];
            }
    
            // Save the updated locations back to LocalStorage
            LocalStorage.saveItem<iDeliveryLocation[]>('deli-locations', newLocations);
    
            // Update the state with the new locations
            setDeliveryLocations(newLocations);

        } catch (error) {
            // Save the updated locations back to LocalStorage
            LocalStorage.saveItem<iDeliveryLocation[]>('deli-locations', [updatedLocation]);
            
            // Update the state with the new locations
            setDeliveryLocations([updatedLocation]);
        }


    }, [location.latitude, location.longitude]);

    return (
        <GeolocationContext.Provider value={{ 
            userAddress, 
            location, 
            deliveryLocations,
            findClosestStoreToCustomer, 
            handleFetchLocationFromAddress,
            saveUserLocationAddress
        }}>
            { children }
        </GeolocationContext.Provider>
    )
}

export const useGeolocation = () => {
    const context = useContext(GeolocationContext);
    if (!context) {
        throw Error('useGeolocation must be used within a GeolocationProvider');
    }
    return context;
};